import React, { useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";
import ApiURl from "../../controllers/Api";

function ToastStack({ toasts, onRemove }) {
  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
                ? "border-red-200 bg-red-50 text-red-900"
                : "border-[#b29063]/20 bg-[#b29063]/10 text-[#b29063]/90";

          return (
            <div
              key={t.id}
              className={[
                "pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border bg-white/60 px-2 py-1 text-xs font-semibold hover:bg-white"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toDatetimeLocal(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (s.includes("T")) return s.slice(0, 16);
  if (s.includes(" ")) return s.replace(" ", "T").slice(0, 16);
  return s;
}

function UpdateProduct() {
  const [toasts, setToasts] = useState([]);
  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((p) => [...p, { id, type, message, title }]);
    window.setTimeout(() => {
      setToasts((p) => p.filter((x) => x.id !== id));
    }, 3000);
  };
  const removeToast = (id) => setToasts((p) => p.filter((x) => x.id !== id));

  const [catData, setCatData] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsInCategory, setProductsInCategory] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [categorySizeGroupId, setCategorySizeGroupId] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");

  const [productData, setProductData] = useState({
    name: "",
    description: "",
    stock_quantity: "0",
    product_type: "size_based",
    productPrice: "",
  });

  const [originalProductData, setOriginalProductData] = useState({});
  const [existingImages, setExistingImages] = useState([]);
  const [originalExistingImages, setOriginalExistingImages] = useState([]);
  const [images, setImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  const [sizePool, setSizePool] = useState([]);
  const [selectedSizeRows, setSelectedSizeRows] = useState([]);
  const [deletedPriceIds, setDeletedPriceIds] = useState([]);

  const [selectedExistingSize, setSelectedExistingSize] = useState("");
  const [customSizeName, setCustomSizeName] = useState("");

  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingSizes, setLoadingSizes] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingSizes, setSavingSizes] = useState(false);
  const skipCategoryResetRef = useRef(false);

  const selectedCategoryName =
    catData.find((c) => String(c.id) === String(selectedCategory))
      ?.category_name || "";

  const distinctProducts = useMemo(() => {
    const map = new Map();
    safeArray(productsInCategory).forEach((p) => {
      if (!p) return;
      const name = String(p.name ?? "").trim();
      if (!name) return;
      if (!map.has(name)) map.set(name, p);
    });
    return [...map.values()];
  }, [productsInCategory]);

  const distinctAllProducts = useMemo(() => {
    const map = new Map();
    safeArray(allProducts).forEach((p) => {
      if (!p) return;
      const id = String(p.product_id ?? "").trim();
      if (!id || map.has(id)) return;
      map.set(id, { ...p, name: p.name || p.product_name });
    });
    return [...map.values()];
  }, [allProducts]);

  const productOptions = selectedCategory ? distinctProducts : distinctAllProducts;

  const hasProductDetailsChanged = useMemo(() => {
    return JSON.stringify(productData) !== JSON.stringify(originalProductData);
  }, [productData, originalProductData]);

  const hasImagesChanged = useMemo(() => {
    return (
      images.length > 0 ||
      imagesToDelete.length > 0 ||
      JSON.stringify(existingImages) !== JSON.stringify(originalExistingImages)
    );
  }, [images, imagesToDelete, existingImages, originalExistingImages]);

  const assignedSizeNames = useMemo(() => {
    return selectedSizeRows.map((r) => String(r.size).trim().toLowerCase());
  }, [selectedSizeRows]);

  const addableExistingSizes = useMemo(() => {
    return sizePool.filter(
      (size) => !assignedSizeNames.includes(String(size).trim().toLowerCase()),
    );
  }, [sizePool, assignedSizeNames]);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCats(true);
      try {
        const response = await $.getJSON(`${ApiURl}/getMainCategory.php`);
        setCatData(safeArray(response?.data));
      } catch (e) {
        setCatData([]);
        toast("error", "Failed to fetch categories.");
      } finally {
        setLoadingCats(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await $.getJSON(`${ApiURl}/getProducts.php`);
        const rows = safeArray(response?.products || response?.data).map((item) => ({
          ...item,
          name: item.name || item.product_name,
        }));
        setAllProducts(rows);
      } catch (e) {
        setAllProducts([]);
        toast("error", "Failed to fetch products.");
      }
    };

    fetchAllProducts();
  }, []);

  useEffect(() => {
    setCategorySizeGroupId(selectedCategory || "");

    if (!skipCategoryResetRef.current) {
      setSelectedProduct("");
      setProductsInCategory([]);
      resetProductEditor();
    }
  }, [selectedCategory]);

  useEffect(() => {
    const keepDirectSelection = skipCategoryResetRef.current;

    if (!keepDirectSelection) {
      setSelectedProduct("");
      setProductsInCategory([]);
      resetProductEditor();
    }

    if (!selectedCategory) {
      skipCategoryResetRef.current = false;
      return;
    }

    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await $.getJSON(
          `${ApiURl}/getProducts.php?mainCatId=${selectedCategory}`,
        );
        const rows = safeArray(response?.products || response?.data).map((item) => ({
          ...item,
          name: item.name || item.product_name,
        }));
        setProductsInCategory(rows);
      } catch (e) {
        setProductsInCategory([]);
        toast("error", "Failed to fetch products.");
      } finally {
        setLoadingProducts(false);
        if (keepDirectSelection) {
          skipCategoryResetRef.current = false;
        }
      }
    };

    fetchProducts();
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedProduct || !selectedCategory || !categorySizeGroupId) {
      resetProductEditor();
      return;
    }

    loadProductDetails();
    loadSizeEditorData();
  }, [selectedProduct, selectedCategory, categorySizeGroupId]);

  function resetProductEditor() {
      setProductData({
        name: "",
        description: "",
        stock_quantity: "0",
        product_type: "size_based",
        productPrice: "",
    });
    setOriginalProductData({});
    setExistingImages([]);
    setOriginalExistingImages([]);
    setImages([]);
    setImagesToDelete([]);
    setSizePool([]);
    setSelectedSizeRows([]);
    setDeletedPriceIds([]);
    setSelectedExistingSize("");
    setCustomSizeName("");
  }

  async function loadProductDetails() {
    setLoadingDetails(true);
    try {
      const productResp = await $.getJSON(
        `${ApiURl}/getProducts.php?product=${selectedProduct}`,
      );

      if (productResp?.success && productResp?.products?.[0]) {
        const product = productResp.products[0];

        const next = {
          name: product?.name ?? "",
          description: product?.description ?? "",
          stock_quantity: product?.stock_quantity ?? "0",
          product_type: product?.product_type ?? "size_based",
          productPrice: product?.productPrice ?? "",
        };

        setProductData(next);
        setOriginalProductData(next);

        if (product?.img_path) {
          try {
            const arr =
              typeof product.img_path === "string"
                ? JSON.parse(product.img_path)
                : Array.isArray(product.img_path)
                  ? product.img_path
                  : [];
            setExistingImages(arr);
            setOriginalExistingImages(arr);
          } catch {
            setExistingImages([]);
            setOriginalExistingImages([]);
          }
        }
      }
    } catch (e) {
      toast("error", "Failed to fetch product details.");
    } finally {
      setLoadingDetails(false);
    }
  }

  async function loadSizeEditorData() {
    setLoadingSizes(true);
    try {
      const resp = await $.getJSON(
        `${ApiURl}/getSizeEditorData.php?main_category_id=${selectedCategory}&sub_category_id=${categorySizeGroupId}&product_id=${selectedProduct}`,
      );

      if (!resp?.success) {
        throw new Error(resp?.message || "Failed to fetch size data");
      }

      setSizePool(safeArray(resp?.size_pool));

      const rows = safeArray(resp?.assigned_sizes).map((row) => ({
        localId: `existing_${row.price_id}`,
        price_id: Number(row.price_id) || 0,
        size: row.size || "",
        original_size: row.original_size || row.size || "",
        price: row.price ?? "",
        single_discount_type: row.single_discount_type || "",
        single_discount_value: row.single_discount_value ?? "",
        single_discount_start: toDatetimeLocal(row.single_discount_start),
        single_discount_end: toDatetimeLocal(row.single_discount_end),
        is_available: Number(row.is_available ?? 1),
        bulk_discounts: row.bulk_discounts || [],
        isNew: false,
      }));

      setSelectedSizeRows(rows);
      setDeletedPriceIds([]);
    } catch (e) {
      console.error(e);
      setSizePool([]);
      setSelectedSizeRows([]);
      toast("error", "Failed to fetch size editor data.");
    } finally {
      setLoadingSizes(false);
    }
  }

  function handleProductDataChange(e) {
    const { name, value } = e.target;
    setProductData((prev) => ({ ...prev, [name]: value }));
  }

  function handleProductSelection(value) {
    setSelectedProduct(value);

    if (!value) {
      resetProductEditor();
      return;
    }

    const product = distinctAllProducts.find(
      (item) => String(item.product_id) === String(value),
    );

    if (!product) return;

    const nextCategory =
      product.category_id ||
      product.product_category_id ||
      product.main_category_id ||
      "";
    const nextSizeGroup =
      product.sub_category_id ||
      product.sub_cat_id ||
      nextCategory;

    if (nextCategory && String(nextCategory) !== String(selectedCategory)) {
      skipCategoryResetRef.current = true;
      setSelectedCategory(String(nextCategory));
    }

    if (nextSizeGroup) {
      setCategorySizeGroupId(String(nextSizeGroup));
    }
  }

  function handleImageChange(e) {
    const files = Array.from(e.target.files || []);
    const total = existingImages.length + images.length + files.length;
    if (total > 5) {
      toast("error", "Maximum 5 images allowed.");
      return;
    }
    setImages((prev) => [...prev, ...files]);
  }

  function handleRemoveExistingImage(img) {
    setImagesToDelete((prev) => [...prev, img]);
    setExistingImages((prev) => prev.filter((x) => x !== img));
  }

  function handleRemoveNewImage(index) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  function addExistingSizeToProduct() {
    const size = String(selectedExistingSize || "").trim();
    if (!size) {
      toast("info", "Select a size first.");
      return;
    }

    const exists = selectedSizeRows.some(
      (r) => String(r.size).trim().toLowerCase() === size.toLowerCase(),
    );
    if (exists) {
      toast("info", "This size is already added.");
      return;
    }

    setSelectedSizeRows((prev) => [
      ...prev,
      {
        localId: `new_existing_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        price_id: 0,
        size,
        original_size: size,
        price: "",
        single_discount_type: "",
        single_discount_value: "",
        single_discount_start: "",
        single_discount_end: "",
        is_available: 1,
        bulk_discounts: [],
        isNew: true,
      },
    ]);

    setSelectedExistingSize("");
  }

  function addCustomSizeToProduct() {
    const size = String(customSizeName || "").trim();
    if (!size) {
      toast("error", "Enter a new size name.");
      return;
    }

    const exists = selectedSizeRows.some(
      (r) => String(r.size).trim().toLowerCase() === size.toLowerCase(),
    );
    if (exists) {
      toast("error", "This size already exists on the product.");
      return;
    }

    setSelectedSizeRows((prev) => [
      ...prev,
      {
        localId: `new_custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        price_id: 0,
        size,
        original_size: size,
        price: "",
        single_discount_type: "",
        single_discount_value: "",
        single_discount_start: "",
        single_discount_end: "",
        is_available: 1,
        bulk_discounts: [],
        isNew: true,
      },
    ]);

    setCustomSizeName("");
  }

  function updateRow(localId, field, value) {
    setSelectedSizeRows((prev) =>
      prev.map((row) =>
        row.localId === localId ? { ...row, [field]: value } : row,
      ),
    );
  }

  function updateBulkDiscount(rowLocalId, discountIndex, field, value) {
    setSelectedSizeRows((prev) =>
      prev.map((row) => {
        if (row.localId !== rowLocalId) return row;
        
        const updatedBulkDiscounts = [...(row.bulk_discounts || [])];
        if (!updatedBulkDiscounts[discountIndex]) {
          updatedBulkDiscounts[discountIndex] = {};
        }
        updatedBulkDiscounts[discountIndex][field] = value;
        
        return { ...row, bulk_discounts: updatedBulkDiscounts };
      }),
    );
  }

  function addBulkDiscount(rowLocalId) {
    setSelectedSizeRows((prev) =>
      prev.map((row) => {
        if (row.localId !== rowLocalId) return row;
        
        const newBulkDiscount = {
          min_quantity: "",
          discounted_price: "",
          discount_type: "amount",
          start: "",
          end: "",
        };
        
        return {
          ...row,
          bulk_discounts: [...(row.bulk_discounts || []), newBulkDiscount],
        };
      }),
    );
  }

  function removeBulkDiscount(rowLocalId, discountIndex) {
    setSelectedSizeRows((prev) =>
      prev.map((row) => {
        if (row.localId !== rowLocalId) return row;
        
        const updatedBulkDiscounts = [...(row.bulk_discounts || [])];
        updatedBulkDiscounts.splice(discountIndex, 1);
        
        return { ...row, bulk_discounts: updatedBulkDiscounts };
      }),
    );
  }

  function removeRow(row) {
    if (row.price_id > 0) {
      setDeletedPriceIds((prev) => [...prev, row.price_id]);
    }
    setSelectedSizeRows((prev) =>
      prev.filter((x) => x.localId !== row.localId),
    );
  }

  async function handleDetailsAndImagesSubmit(e) {
    if (e) e.preventDefault();

    if (!selectedProduct) {
      toast("error", "Please select a product first.");
      return;
    }

    if (!hasProductDetailsChanged && !hasImagesChanged) {
      toast("info", "No changes in product details/images.");
      return;
    }

    setSavingDetails(true);

    try {
      const formData = new FormData();
      formData.append("product_id", selectedProduct);
      formData.append("name", productData.name || "");
      formData.append("description", productData.description || "");
      formData.append("currentCategoryId", selectedCategory || "");
      formData.append("currentSubCategoryId", categorySizeGroupId || selectedCategory || "");
      formData.append("stock_quantity", productData.stock_quantity || "0");
      formData.append("product_type", productData.product_type || "size_based");
      formData.append("productPrice", productData.productPrice || "");

      imagesToDelete.forEach((img) => formData.append("delete_images[]", img));
      images.forEach((img) => formData.append("images[]", img));

      const response = await $.ajax({
        url: `${ApiURl}/updateProductimg.php`,
        type: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        toast("success", response.message || "Product details updated.");
        setOriginalProductData({ ...productData });
        setImages([]);
        setImagesToDelete([]);
        await loadProductDetails();
      } else {
        toast(
          "error",
          response?.message || "Failed to update product details.",
        );
      }
    } catch (err) {
      console.error(err);
      toast("error", "Update failed for product details.");
    } finally {
      setSavingDetails(false);
    }
  }

  async function handleSaveSizes(e) {
    if (e) e.preventDefault();

    if (!selectedCategory || !selectedProduct) {
      toast("error", "Select category and product first.");
      return;
    }

    if (selectedSizeRows.length === 0 && deletedPriceIds.length === 0) {
      toast("info", "No size changes to save.");
      return;
    }

    for (const row of selectedSizeRows) {
      const size = String(row.size || "").trim();
      const price = Number(row.price);

      if (!size) {
        toast("error", "Size name cannot be empty.");
        return;
      }

      if (!Number.isFinite(price) || price <= 0) {
        toast("error", `Enter valid price for size "${size}".`);
        return;
      }

      if (row.bulk_discounts && row.bulk_discounts.length > 0) {
        for (const discount of row.bulk_discounts) {
          if (discount.min_quantity && discount.discounted_price) {
            const minQty = parseInt(discount.min_quantity);
            const discPrice = parseFloat(discount.discounted_price);
            
            if (minQty <= 0) {
              toast("error", `Min quantity must be greater than 0 for size "${size}"`);
              return;
            }
            
            if (discPrice <= 0) {
              toast("error", `Discounted price must be greater than 0 for size "${size}"`);
              return;
            }
            
            if (discount.discount_type === 'percentage' && discPrice > 100) {
              toast("error", `Percentage discount cannot exceed 100 for size "${size}"`);
              return;
            }
          }
        }
      }
    }

    setSavingSizes(true);

    try {
      const payload = {
        main_category_id: Number(selectedCategory),
        sub_category_id: Number(categorySizeGroupId),
        product_id: Number(selectedProduct),
        deleted_price_ids: deletedPriceIds,
        rows: selectedSizeRows.map((row) => ({
          price_id: Number(row.price_id) || 0,
          size: String(row.size || "").trim(),
          original_size: String(row.original_size || row.size || "").trim(),
          price: Number(row.price),
          single_discount_type: row.single_discount_type || "",
          single_discount_value:
            row.single_discount_value === ""
              ? ""
              : Number(row.single_discount_value),
          single_discount_start: row.single_discount_start || "",
          single_discount_end: row.single_discount_end || "",
          is_available: Number(row.is_available ?? 1),
          bulk_discounts: (row.bulk_discounts || []).map(d => ({
            min_quantity: parseInt(d.min_quantity) || 0,
            discounted_price: parseFloat(d.discounted_price) || 0,
            discount_type: d.discount_type || 'amount',
            start: d.start || '',
            end: d.end || ''
          })).filter(d => d.min_quantity > 0 && d.discounted_price > 0)
        })),
      };

      const response = await $.ajax({
        url: `${ApiURl}/saveProductSizes.php`,
        type: "POST",
        data: JSON.stringify(payload),
        contentType: "application/json",
        dataType: "json",
      });

      if (response?.success) {
        toast("success", response.message || "Sizes/prices updated.");
        await loadSizeEditorData();
      } else {
        toast("error", response?.message || "Failed to save sizes.");
      }
    } catch (err) {
      console.error(err);
      toast("error", "Failed to save sizes.");
    } finally {
      setSavingSizes(false);
    }
  }

  return (
    <div className="update-product-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] font-[Kanit] text-[#2B0000]">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <div className="mx-auto w-full max-w-[1500px] px-3 py-4 sm:px-5 sm:py-6 lg:px-7">
        {/* Header */}
        <div className="mb-5 overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.12),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:p-7">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">
                Update Product
              </h1>
              <p className="mt-2 max-w-3xl text-xs leading-6 text-[#6f5e57] sm:text-sm">
                Update product details, sizes, prices and optional discounts.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col gap-5 xl:flex-row">
          {/* Left Column - Main Content */}
          <div className="min-w-0 flex-1 space-y-5">
            {/* Product Selection Card */}
            <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_18px_50px_rgba(43,0,0,0.07)] sm:p-6">
              <h2 className="mb-5 font-serif text-xl font-medium text-[#2B0000] sm:text-2xl">
                Product Selection
              </h2>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                    Category Filter
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  >
                    <option value="">
                      {loadingCats ? "Loading..." : "All Categories"}
                    </option>
                    {catData.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.category_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                    Product <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => handleProductSelection(e.target.value)}
                    disabled={loadingProducts && selectedCategory}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10 disabled:cursor-not-allowed disabled:bg-[#eee5dd]"
                  >
                    <option value="">
                      {selectedCategory && loadingProducts
                          ? "Loading..."
                          : selectedCategory
                          ? "Select Product"
                          : "Select Product Directly"}
                    </option>
                    {productOptions.map((p) => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {selectedProduct && (
              <>
                {/* Product Details Card */}
                <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_18px_50px_rgba(43,0,0,0.07)] sm:p-6">
                  <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <h2 className="font-serif text-xl font-medium text-[#2B0000] sm:text-2xl">
                      Product Details & Images
                    </h2>
                    <button
                      type="button"
                      onClick={handleDetailsAndImagesSubmit}
                      disabled={savingDetails}
                      className="w-full rounded-xl border border-[#d4af37]/20 bg-[#6f7f5e] px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(68,93,58,0.18)] transition hover:-translate-y-0.5 hover:bg-[#5f704f] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                      {savingDetails ? "Saving..." : "Update Details"}
                    </button>
                  </div>

                  {loadingDetails ? (
                    <div className="py-10 text-center text-sm text-[#7d6c64]">
                      Loading details...
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Product Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={productData.name || ""}
                            onChange={handleProductDataChange}
                            className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Description
                          </label>
                          <textarea
                            name="description"
                            value={productData.description || ""}
                            onChange={handleProductDataChange}
                            rows="3"
                            className="mt-2 w-full resize-none rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Stock Quantity
                          </label>
                          <input
                            type="number"
                            name="stock_quantity"
                            value={productData.stock_quantity || "0"}
                            onChange={handleProductDataChange}
                            min="0"
                            className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Product Type
                          </label>
                          <select
                            name="product_type"
                            value={productData.product_type || "size_based"}
                            onChange={handleProductDataChange}
                            className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          >
                            <option value="size_based">Size Based</option>
                            <option value="simple">Simple</option>
                          </select>
                        </div>
                      </div>

                      {/* Images Section */}
                      <div className="mt-6 border-t border-[#dfd0c4] pt-5">
                        <h3 className="mb-4 font-serif text-lg font-medium text-[#2B0000]">
                          Product Images (Max 5)
                        </h3>

                        {existingImages.length > 0 && (
                          <div className="mb-4">
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                              {existingImages.map((img, idx) => (
                                <div key={idx} className="group relative overflow-hidden rounded-xl border border-[#decfc3] bg-white shadow-[0_8px_22px_rgba(43,0,0,0.05)]">
                                  <img
                                    src={`${ApiURl}/images/${img}`}
                                    alt={`Product ${idx + 1}`}
                                    className="h-28 w-full bg-[#fffaf5] object-contain transition duration-500 group-hover:scale-105 sm:h-32"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveExistingImage(img)}
                                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-red-200 bg-white/95 text-sm text-red-600 opacity-100 shadow-sm transition hover:bg-red-600 hover:text-white sm:opacity-0 sm:group-hover:opacity-100"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {images.length > 0 && (
                          <div className="mb-4">
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                              {images.map((img, idx) => (
                                <div key={idx} className="group relative overflow-hidden rounded-xl border border-[#decfc3] bg-white shadow-[0_8px_22px_rgba(43,0,0,0.05)]">
                                  <img
                                    src={URL.createObjectURL(img)}
                                    alt={`New ${idx + 1}`}
                                    className="h-28 w-full bg-[#fffaf5] object-contain transition duration-500 group-hover:scale-105 sm:h-32"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveNewImage(idx)}
                                    className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-red-200 bg-white/95 text-sm text-red-600 opacity-100 shadow-sm transition hover:bg-red-600 hover:text-white sm:opacity-0 sm:group-hover:opacity-100"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <label className="mb-2 block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Add New Images
                          </label>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full cursor-pointer rounded-xl border border-[#d6c4b5] bg-white p-2 text-sm text-[#6f5e57] file:mr-4 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[#2B0000] file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-[#f4dfae] hover:file:bg-[#4A090B]"
                          />
                          <p className="mt-2 text-xs text-[#7d6c64]">
                            You can upload up to {5 - (existingImages.length + images.length)} more images
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Sizes & Prices Card */}
                <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_18px_50px_rgba(43,0,0,0.07)] sm:p-6">
                  <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <div>
                      <h2 className="font-serif text-xl font-medium text-[#2B0000] sm:text-2xl">
                        Sizes, Prices & Optional Discounts
                      </h2>
                      <p className="mt-2 max-w-3xl text-xs leading-6 text-[#6f5e57] sm:text-sm">
                        Add sizes and prices. Single and bulk discounts are optional.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveSizes}
                      disabled={savingSizes}
                      className="w-full rounded-xl border border-[#d4af37]/20 bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] shadow-[0_10px_26px_rgba(43,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                      {savingSizes ? "Saving..." : "Save All Changes"}
                    </button>
                  </div>

                  {loadingSizes ? (
                    <div className="py-10 text-center text-sm text-[#7d6c64]">
                      Loading sizes...
                    </div>
                  ) : (
                    <>
                      {/* Add Size Options */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-[18px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f4e9df)] p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
                          <label className="mb-2 block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Add Existing Size
                          </label>
                          <select
                            value={selectedExistingSize}
                            onChange={(e) => setSelectedExistingSize(e.target.value)}
                            className="mb-3 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          >
                            <option value="">Select size</option>
                            {addableExistingSizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={addExistingSizeToProduct}
                            className="w-full rounded-xl bg-[#8f6f38] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-[#765a2d]"
                          >
                            Add Existing Size
                          </button>
                        </div>

                        <div className="rounded-[18px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f4e9df)] p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)]">
                          <label className="mb-2 block text-xs font-semibold text-[#4a352f] sm:text-sm">
                            Add New Size
                          </label>
                          <input
                            type="text"
                            value={customSizeName}
                            onChange={(e) => setCustomSizeName(e.target.value)}
                            placeholder="Example: Big / XXL / 72 Round"
                            className="mb-3 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                          />
                          <button
                            type="button"
                            onClick={addCustomSizeToProduct}
                            className="w-full rounded-xl bg-[#6f7f5e] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-[#5f704f]"
                          >
                            Add New Size
                          </button>
                        </div>
                      </div>

                      {/* Size Rows */}
                      <div className="space-y-4">
                        {selectedSizeRows.length === 0 ? (
                          <div className="rounded-[18px] border-2 border-dashed border-[#d4af37]/35 bg-[#fbf3eb] p-10 text-center">
                            <p className="text-sm text-[#7d6c64]">
                              No sizes attached to this product yet.
                            </p>
                            <p className="mt-1 text-xs text-[#9a8981]">
                              Use the options above to add sizes.
                            </p>
                          </div>
                        ) : (
                          selectedSizeRows.map((row) => (
                            <div
                              key={row.localId}
                              className="rounded-[20px] border border-[#decfc3] bg-white p-4 shadow-[0_10px_28px_rgba(43,0,0,0.05)] transition hover:border-[#d4af37]/40 hover:shadow-[0_16px_36px_rgba(43,0,0,0.09)]"
                            >
                              {/* Basic Size Info */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Size {row.isNew ? "(New)" : ""}
                                  </label>
                                  <input
                                    type="text"
                                    value={row.size}
                                    onChange={(e) => updateRow(row.localId, "size", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  />
                                </div>

                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Price (₹)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={row.price}
                                    onChange={(e) => updateRow(row.localId, "price", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  />
                                </div>

                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Single Discount Type
                                  </label>
                                  <select
                                    value={row.single_discount_type || ""}
                                    onChange={(e) => updateRow(row.localId, "single_discount_type", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  >
                                    <option value="">No Discount</option>
                                    <option value="amount">Fixed Amount</option>
                                    <option value="percentage">Percentage</option>
                                  </select>
                                </div>

                                <div className="flex items-end">
                                  <button
                                    type="button"
                                    onClick={() => removeRow(row)}
                                    className="w-full rounded-lg border border-red-200 bg-[#fff4f1] px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-600 hover:text-white"
                                  >
                                    Remove Size
                                  </button>
                                </div>
                              </div>

                              {/* Discount Details */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Discount Value
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={row.single_discount_value}
                                    onChange={(e) => updateRow(row.localId, "single_discount_value", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  />
                                </div>

                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Discount Start
                                  </label>
                                  <input
                                    type="datetime-local"
                                    value={row.single_discount_start || ""}
                                    onChange={(e) => updateRow(row.localId, "single_discount_start", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  />
                                </div>

                                <div>
                                  <label className="mb-1.5 block text-xs font-semibold text-[#5d4a43]">
                                    Discount End
                                  </label>
                                  <input
                                    type="datetime-local"
                                    value={row.single_discount_end || ""}
                                    onChange={(e) => updateRow(row.localId, "single_discount_end", e.target.value)}
                                    className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                                  />
                                </div>
                              </div>

<div className="mt-4 border-t border-[#e3d6cb] pt-4">
  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
    <h4 className="text-sm font-semibold text-[#4a352f]">
      Bulk Quantity Discounts
    </h4>
    <button
      type="button"
      onClick={() => addBulkDiscount(row.localId)}
      className="w-full rounded-lg border border-[#d4af37]/25 bg-[#fff5dc] px-3 py-2 text-xs font-semibold text-[#7d5a19] transition hover:bg-[#f8e8b5] sm:w-auto"
    >
      + Add Bulk Discount
    </button>
  </div>

  {row.bulk_discounts && row.bulk_discounts.length > 0 ? (
    <div className="space-y-3">
      {row.bulk_discounts.map((discount, idx) => (
        <div key={idx} className="rounded-xl border border-[#e0d2c7] bg-[#fbf3eb] p-4">
          <div className="flex flex-col gap-3">
            {/* First Row - Min Quantity and Discount Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs text-[#75645d]">
                  Min Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={discount.min_quantity}
                  onChange={(e) => updateBulkDiscount(row.localId, idx, "min_quantity", e.target.value)}
                  className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  placeholder="Min qty"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs text-[#75645d]">
                  Discount Value
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discount.discounted_price}
                  onChange={(e) => updateBulkDiscount(row.localId, idx, "discounted_price", e.target.value)}
                  className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  placeholder="Value"
                />
              </div>
            </div>

            {/* Second Row - Type, Start Date, End Date and Remove Button */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="mb-1.5 block text-xs text-[#75645d]">
                  Type
                </label>
                <select
                  value={discount.discount_type || "amount"}
                  onChange={(e) => updateBulkDiscount(row.localId, idx, "discount_type", e.target.value)}
                  className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="amount">Fixed Amount</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs text-[#75645d]">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={discount.start || ""}
                  onChange={(e) => updateBulkDiscount(row.localId, idx, "start", e.target.value)}
                  className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs text-[#75645d]">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={discount.end || ""}
                  onChange={(e) => updateBulkDiscount(row.localId, idx, "end", e.target.value)}
                  className="w-full rounded-lg border border-[#d6c4b5] bg-white px-3 py-2.5 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeBulkDiscount(row.localId, idx)}
                  className="flex w-full items-center justify-center gap-1 rounded-lg border border-red-200 bg-[#fff2ef] px-3 py-2.5 text-xs font-semibold text-red-600 transition hover:bg-red-600 hover:text-white"
                >
                  <span>Remove</span>
                  <span>✕</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-center text-sm italic text-[#9a8981] sm:text-left">
      No bulk discounts added yet.
    </p>
  )}
</div>

                              {row.price_id > 0 && (
                                <div className="mt-2 text-xs text-[#9a8981]">
                                  Price Row ID: {row.price_id}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Column - Summary Card */}
          <div className="xl:w-[340px] 2xl:w-[390px]">
            <div className="sticky top-20 overflow-hidden rounded-[24px] border border-[#d4af37]/22 bg-[#fffaf5] p-4 shadow-[0_22px_55px_rgba(43,0,0,0.10)] sm:p-6">
              <h3 className="mb-5 font-serif text-xl font-medium text-[#2B0000] sm:text-2xl">Summary</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-[#eadfd5] py-3">
                  <span className="text-sm text-[#6f5e57]">Product</span>
                  <span className="max-w-[60%] truncate text-right text-sm font-semibold text-[#2B0000]" title={productData.name}>
                    {productData.name || "—"}
                  </span>
                </div>
                
                <div className="flex items-center justify-between border-b border-[#eadfd5] py-3">
                  <span className="text-sm text-[#6f5e57]">Category</span>
                  <span className="max-w-[60%] truncate text-right text-sm font-semibold text-[#2B0000]" title={selectedCategoryName}>
                    {selectedCategoryName || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-[#eadfd5] py-3">
                  <span className="text-sm text-[#6f5e57]">Sizes</span>
                  <span className="text-sm font-semibold text-[#2B0000]">
                    {selectedSizeRows.length}
                  </span>
                </div>
                
                <div className="flex items-center justify-between border-b border-[#eadfd5] py-3">
                  <span className="text-sm text-[#6f5e57]">Images</span>
                  <span className="text-sm font-semibold text-[#2B0000]">
                    {existingImages.length + images.length} / 5
                  </span>
                </div>
                
                <div className="flex items-center justify-between border-b border-[#eadfd5] py-3">
                  <span className="text-sm text-[#6f5e57]">Stock</span>
                  <span className="text-sm font-semibold text-[#2B0000]">
                    {productData.stock_quantity || 0}
                  </span>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={handleDetailsAndImagesSubmit}
                  disabled={savingDetails}
                  className="w-full rounded-xl bg-[#6f7f5e] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-[#5f704f] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingDetails ? "Saving..." : "Update Details"}
                </button>

                <button
                  type="button"
                  onClick={handleSaveSizes}
                  disabled={savingSizes}
                  className="w-full rounded-xl bg-[#2B0000] px-4 py-3 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingSizes ? "Saving..." : "Save Sizes & Prices"}
                </button>
              </div>

              {/* Quick Info */}
              {selectedProduct && (
                <div className="mt-5 border-t border-[#eadfd5] pt-4">
                  <p className="text-xs leading-5 text-[#7d6c64]">
                    <span className="font-semibold">Note:</span> Changes to sizes and prices will be applied immediately after saving.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .update-product-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.11), transparent 29%),
            radial-gradient(circle at 96% 85%, rgba(43,0,0,.045), transparent 31%),
            #F7EFE7;
        }

        .update-product-luxury input,
        .update-product-luxury select,
        .update-product-luxury textarea,
        .update-product-luxury button {
          font: inherit;
        }

        .update-product-luxury textarea {
          min-height: 115px;
        }

        .update-product-luxury input[type="datetime-local"] {
          color-scheme: light;
        }

        .update-product-luxury ::selection {
          color: #fff7e7;
          background: #6f2929;
        }

        .update-product-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(111,41,41,.32) rgba(212,175,55,.07);
        }

        .update-product-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .update-product-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .update-product-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(111,41,41,.32);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .update-product-luxury {
            padding-bottom: 24px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .update-product-luxury *,
          .update-product-luxury *::before,
          .update-product-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default UpdateProduct;